import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import { HiOutlinePlus, HiOutlineSearch } from "react-icons/hi";
import { useAuth } from "./AuthContext";
import { useAuthenticatedRequest } from "./hooks/useAuthenticatedRequest";
import { getDefaultOwnerOrganisationId, getUserOrganisationIds, withOwnerOrganisation } from "./utils/ownership";
import { showApiErrorToast } from "./utils/showApiToast";
import OrganisationSelect from "./OrganisationSelect";
import Navbar from "./Navbar";
import WatermarkBackground from "./components/WatermarkBackground";
import { useClocks } from "./hooks/useClocks";
import { usePlacingBook } from "./hooks/usePlacingBook";
import { useSeenStages } from "./hooks/useSeenStages";
import { getProducts } from "./services/MrcaiAPI";
import PortalStageBlocks, { STAGE_BLOCKS, brokerStageBlocks } from "./components/placing/PortalStageBlocks";
import ClockChips from "./components/poc/ClockChips";
import WorkflowTasks from "./components/poc/WorkflowTasks";
import DealsList from "./components/poc/DealsList";
import { dealRows, filterDealRows } from "./components/poc/dealRows";

const panel = { borderWidth: "1px", borderRadius: "md", bg: "whiteAlpha.800" };

/** A searchable set of cards (parties, registers), each opening its record. */
const CardList = ({ items, loading, onOpen, onAdd, addLabel, sub, placeholder }) => {
  const [search, setSearch] = useState("");
  const text = search.trim().toLowerCase();
  const shown = (items || []).filter((i) => !text || [i.name, sub?.(i)].some((v) => String(v || "").toLowerCase().includes(text)));
  return (
    <Box {...panel} p={4}>
      <HStack justify="space-between" mb={3} flexWrap="wrap" rowGap={2}>
        <InputGroup size="sm" maxW="320px">
          <InputLeftElement pointerEvents="none"><HiOutlineSearch color="gray" /></InputLeftElement>
          <Input bg="white" placeholder={placeholder} value={search} onChange={(e) => setSearch(e.target.value)} />
        </InputGroup>
        <Button size="sm" colorScheme="blue" leftIcon={<HiOutlinePlus />} onClick={onAdd}>{addLabel}</Button>
      </HStack>
      {loading ? (
        <Spinner />
      ) : shown.length === 0 ? (
        <Text fontSize="sm" color="gray.500">{text ? "Nothing matches." : "None yet."}</Text>
      ) : (
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, xl: 4 }} spacing={3}>
          {shown.map((i) => (
            <Box key={i._id} as="button" type="button" textAlign="left" bg="whiteAlpha.900" borderWidth="1px" borderRadius="md" p={3}
              _hover={{ borderColor: "blue.300", boxShadow: "sm" }} onClick={() => onOpen(i)}>
              <Text fontWeight="semibold" noOfLines={1}>{i.name || "Untitled"}</Text>
              {sub && <Text fontSize="xs" color="gray.500" noOfLines={1}>{sub(i) || "—"}</Text>}
            </Box>
          ))}
        </SimpleGrid>
      )}
    </Box>
  );
};

/**
 * The PoC home page. Dashboard: your deals and how their placements are
 * going (the four placing stages, needs you, new), your workflow tasks and
 * the platform clocks. Parties and Registers have their own tabs.
 */
const Poc = () => {
  const { user } = useAuth();
  const { get, post } = useAuthenticatedRequest();
  const navigate = useNavigate();
  const toast = useToast();
  const isUserSuperAdmin = user?.platformUser?.isSuperAdmin === "true";
  const isUserAdmin = user?.platformUser?.isAdmin === "true";
  const isProductDesigner =
    user?.platformUser?.roles?.some((role) => (typeof role === "string" ? role : role.role) === "product_designer") || false;
  const canSeeParties = isProductDesigner || isUserAdmin || isUserSuperAdmin;

  // Deals, product names and placements: refreshes every minute while the page is open
  const { loading, refreshing, lastUpdated, error, records, summaries, dealsById, productNames, reload } = usePlacingBook();
  const { seenAt, sinceLastVisit, markSeen } = useSeenStages("broker");
  const { clocks } = useClocks();

  const [stage, setStage] = useState(null);
  const [needsOnly, setNeedsOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [product, setProduct] = useState("");

  const [parties, setParties] = useState([]);
  const [partyLoading, setPartyLoading] = useState(true);
  const [registers, setRegisters] = useState([]);
  const [registerLoading, setRegisterLoading] = useState(true);
  const [products, setProducts] = useState(null);

  // New deal
  const newDeal = useDisclosure();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({ name: "" });
  const [creating, setCreating] = useState(false);
  const dealOrganisationId = formData.organisationOwnerId || getDefaultOwnerOrganisationId(user);

  useEffect(() => {
    if (error && !records) showApiErrorToast(toast, error, { title: "Couldn't load your deals and placements" });
  }, [error, records, toast]);

  useEffect(() => {
    get("https://registers.poc.mrc.ai/api/registers/headers")
      .then((res) => setRegisters(Array.isArray(res.data) ? res.data : []))
      .catch((err) => showApiErrorToast(toast, err, { title: "Couldn't load the registers" }))
      .finally(() => setRegisterLoading(false));
    if (canSeeParties) {
      get("https://directory.poc.mrc.ai/api/parties")
        .then((res) => setParties(Array.isArray(res.data) ? res.data : []))
        .catch((err) => showApiErrorToast(toast, err, { title: "Couldn't load the parties" }))
        .finally(() => setPartyLoading(false));
    }
    // once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deals = useMemo(() => Object.values(dealsById || {}), [dealsById]);
  const rows = useMemo(() => dealRows(deals, summaries, sinceLastVisit), [deals, summaries, sinceLastVisit]);
  const shown = filterDealRows(rows, { stage, needsOnly, product, search, productNames });
  const blocks = brokerStageBlocks(summaries, seenAt);
  const dealProducts = [...new Set(deals.map((d) => d.productCode).filter(Boolean))].sort();

  const selectStage = (key) => {
    setStage(key);
    if (key) markSeen(key);
  };

  const openNewDeal = () => {
    setSelectedProduct(null);
    setFormData({ name: "" });
    newDeal.onOpen();
    if (!products) {
      getProducts()
        .then(setProducts)
        .catch((err) => {
          setProducts([]);
          showApiErrorToast(toast, err, { title: "Couldn't load the products" });
        });
    }
  };

  const createDeal = async () => {
    if (!dealOrganisationId) return toast({ title: "Choose the organisation that owns this deal", status: "warning" });
    if (!formData.name.trim()) return toast({ title: "Give the deal a name", status: "warning" });
    setCreating(true);
    try {
      await post(
        "https://poc.mrc.ai/api/deals",
        withOwnerOrganisation(
          {
            name: formData.name.trim(),
            type: "",
            placingStageCode: "",
            placingTransactionFunctionCode: "",
            productCode: selectedProduct.code,
            organisationOwnerId: dealOrganisationId,
          },
          user
        )
      );
      toast({ title: "Deal created", status: "success" });
      newDeal.onClose();
      reload();
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't create the deal" });
    } finally {
      setCreating(false);
    }
  };

  const emptyText = stage
    ? `None of your deals has a placement at ${STAGE_BLOCKS.find((b) => b.key === stage).title}.`
    : needsOnly
    ? "Nothing needs you right now."
    : search || product
    ? "No deals match."
    : "No deals yet. Create one with + New deal.";

  return (
    <Box position="relative" minHeight="100vh">
      <WatermarkBackground />
      <Navbar />
      <Box px={{ base: 4, md: 6 }} py={5} maxW="1600px" mx="auto">
        <Tabs variant="line" colorScheme="linkedin" isLazy>
          <TabList>
            <Tab>Dashboard</Tab>
            {canSeeParties && <Tab>Parties</Tab>}
            <Tab>Registers</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between" align="flex-end" flexWrap="wrap" rowGap={2}>
                  <Box>
                    <Heading size="lg">Home</Heading>
                    <Text color="gray.600">Your deals, how their placements are going, and your tasks</Text>
                  </Box>
                  <HStack spacing={3}>
                    <Tooltip label="Refreshes every minute while this page is open" hasArrow>
                      <Text fontSize="xs" color={error ? "red.500" : "gray.500"}>
                        {error ? "Couldn't refresh · showing " : "Updated "}
                        {lastUpdated ? lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                      </Text>
                    </Tooltip>
                    <IconButton size="xs" variant="ghost" aria-label="Refresh now" icon={<RepeatIcon />} onClick={reload} isLoading={refreshing} />
                    <Button colorScheme="blue" size="sm" leftIcon={<HiOutlinePlus />} onClick={openNewDeal}>New deal</Button>
                  </HStack>
                </HStack>

                <ClockChips clocks={clocks} />

                {loading ? (
                  <Spinner />
                ) : (
                  <>
                    <PortalStageBlocks blocks={blocks} selected={stage} onSelect={selectStage} />

                    <Grid templateColumns={{ base: "1fr", xl: "1fr 380px" }} gap={4} alignItems="start">
                      <Box {...panel}>
                        <HStack px={4} py={2} justify="space-between" flexWrap="wrap" rowGap={2}>
                          <HStack spacing={4}>
                            <Heading size="sm">
                              {stage ? `Deals at ${STAGE_BLOCKS.find((b) => b.key === stage).title}` : "Your deals"}
                              <Text as="span" fontWeight="normal" color="gray.500"> · {shown.length}</Text>
                            </Heading>
                            <FormControl display="flex" alignItems="center" w="auto">
                              <Switch id="home-needs-only" size="sm" colorScheme="orange" isChecked={needsOnly} onChange={(e) => setNeedsOnly(e.target.checked)} />
                              <FormLabel htmlFor="home-needs-only" mb={0} ml={2} fontSize="sm">Needs you or new only</FormLabel>
                            </FormControl>
                          </HStack>
                          <HStack spacing={2}>
                            <Input size="sm" w="220px" bg="white" placeholder="Search deal or placement…" value={search} onChange={(e) => setSearch(e.target.value)} />
                            <Select size="sm" w="160px" bg="white" value={product} onChange={(e) => setProduct(e.target.value)}>
                              <option value="">All products</option>
                              {dealProducts.map((p) => <option key={p} value={p}>{productNames[p] || p}</option>)}
                            </Select>
                          </HStack>
                        </HStack>
                        <DealsList rows={shown} productNames={productNames} emptyText={emptyText} />
                      </Box>

                      <VStack align="stretch" spacing={4}>
                        <WorkflowTasks refreshKey={lastUpdated?.getTime()} onError={(err) => showApiErrorToast(toast, err, { title: "Couldn't load your tasks" })} />
                        <Box {...panel} px={4} py={3}>
                          <HStack justify="space-between" mb={2}>
                            <Heading size="sm">Registers</Heading>
                            <Button size="xs" variant="ghost" leftIcon={<HiOutlinePlus />} onClick={() => navigate("/registers/new")}>New</Button>
                          </HStack>
                          {registerLoading ? (
                            <Spinner size="sm" />
                          ) : registers.length === 0 ? (
                            <Text fontSize="sm" color="gray.500">None yet.</Text>
                          ) : (
                            <VStack align="stretch" spacing={0} maxH="240px" overflowY="auto">
                              {registers.map((r) => (
                                <Box key={r._id} as="button" type="button" textAlign="left" px={2} py={1} borderRadius="md" fontSize="sm"
                                  _hover={{ bg: "blackAlpha.50" }} onClick={() => navigate(`/registers/${r._id}`)}>
                                  {r.name || "Untitled register"}
                                </Box>
                              ))}
                            </VStack>
                          )}
                        </Box>
                      </VStack>
                    </Grid>
                  </>
                )}
              </VStack>
            </TabPanel>

            {canSeeParties && (
              <TabPanel px={0}>
                <CardList items={parties} loading={partyLoading} onOpen={(p) => navigate(`/party/${p._id}`)} onAdd={() => navigate("/party/new")}
                  addLabel="New party" sub={(p) => p.roleCode} placeholder="Search parties…" />
              </TabPanel>
            )}

            <TabPanel px={0}>
              <CardList items={registers} loading={registerLoading} onOpen={(r) => navigate(`/registers/${r._id}`)} onAdd={() => navigate("/registers/new")}
                addLabel="New register" placeholder="Search registers…" />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      <Modal isOpen={newDeal.isOpen} onClose={newDeal.onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{selectedProduct ? "New deal" : "Choose a product"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {!selectedProduct ? (
              products === null ? (
                <Spinner />
              ) : products.length === 0 ? (
                <Text fontSize="sm" color="gray.500">No products available.</Text>
              ) : (
                <SimpleGrid columns={1} spacing={2}>
                  {products.map((p) => (
                    <Box key={p._id || p.code} as="button" type="button" textAlign="left" borderWidth="1px" borderRadius="md" p={3}
                      _hover={{ borderColor: "blue.400", boxShadow: "sm" }} onClick={() => setSelectedProduct(p)}>
                      <Text fontWeight="semibold">{p.name}</Text>
                      <Text fontSize="xs" color="gray.500">Code {p.code}</Text>
                      {p.description && <Text fontSize="xs" color="gray.600">{p.description}</Text>}
                    </Box>
                  ))}
                </SimpleGrid>
              )
            ) : (
              <Box as="form" onSubmit={(e) => { e.preventDefault(); createDeal(); }}>
                <Text fontSize="sm" mb={4} color="gray.600">
                  Product: <strong>{selectedProduct.name}</strong> ({selectedProduct.code})
                </Text>
                <Stack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Deal name</FormLabel>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} autoFocus />
                  </FormControl>
                  <OrganisationSelect
                    label="Organisation"
                    size="sm"
                    selectedOrganisationId={dealOrganisationId}
                    onChange={(orgId) => setFormData({ ...formData, organisationOwnerId: orgId || undefined })}
                    isRequired={getUserOrganisationIds(user).length > 1}
                  />
                </Stack>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            {selectedProduct && <Button size="sm" mr={2} onClick={() => setSelectedProduct(null)}>Back</Button>}
            <Button size="sm" mr={2} variant="ghost" onClick={newDeal.onClose}>Cancel</Button>
            {selectedProduct && (
              <Button size="sm" colorScheme="blue" onClick={createDeal} isLoading={creating} isDisabled={!dealOrganisationId}>Create</Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Poc;
